/**
 * Header.tsx - Fundraiser Page Header Component
 *
 * This component renders the main header for fundraiser pages with logo, title, and action buttons.
 * It supports multiple display modes including regular fundraiser view and promoter invite pages.
 * Features sticky behavior with scroll detection and responsive design.
 *
 * Key Features:
 * - Dynamic logo/icon display (image or icon with custom colors)
 * - Fundraiser title and subtitle with badge support
 * - Status badges (pending, expiration warnings)
 * - Donate and share buttons with custom styling
 * - Sticky header with scroll-based styling changes
 * - Promoter invite page specific layout
 *
 * Props:
 * - onDonateClick?: () => void - Donation button click handler
 * - onShareClick?: () => void - Share button click handler
 * - customColors?: object - Custom color theme
 * - isDisabled?: boolean - Whether actions are disabled
 * - isPreview?: boolean - Whether in preview mode
 * - isPromoterInvite?: boolean - Whether on promoter invite page
 *
 * Dependencies: MobX store, color utilities, badge utilities, Lucide icons
 */

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Droplets, Share2 } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
  shouldShowPendingBadge,
  getPendingBadgeText,
} from "../utils/badgesUtils";
import { isLightColor, darkenColor, lightenColor } from "../utils/colorUtils";
import { FundRaiserStatus, IconType } from "../enums";
import { ColorModel, FundraiserCustomColorConfig, MediaModel } from "../models";
import { NavigateFunction } from "react-router-dom";
import { withRouter } from "@mindmemobile/mm-shared-global";

/**
 * Hysteresis thresholds for the sticky header collapse.
 *
 * The header sits in the normal document flow (position: sticky), so collapsing
 * it shrinks the document by exactly 50px (padding 41px -> 16px, top + bottom).
 * The browser's scroll anchoring compensates for that by moving scrollTop by
 * the same amount, which feeds straight back into the scroll handler. With a
 * single `scrollTop > 50` threshold the collapse pushed the scroll position
 * back under the threshold, which expanded the header again - an endless
 * oscillation. The 60px dead band between these two values is wider than that
 * 50px correction, so no layout shift can flip the state back.
 */
const SCROLL_COLLAPSE_AT = 110;
const SCROLL_EXPAND_BELOW = 50;

const HeaderContainer = styled.header<{
  $isScrolled: boolean;
  customColors?: FundraiserCustomColorConfig;
}>`
  position: sticky;
  top: 0;
  z-index: 999;
  width: 100%;
  border-bottom: ${(props) =>
    props.$isScrolled ? "1px solid #e2e8f0" : "none"};
  padding: ${(props) => (props.$isScrolled ? "16px 0" : "41px 0")};
  transition:
    padding 0.3s ease,
    border-bottom-color 0.3s ease,
    box-shadow 0.3s ease;
  background: ${(props) => props.customColors?.background.rgba || "#f8fafc"};
  backdrop-filter: ${(props) => (props.$isScrolled ? "blur(12px)" : "none")};
  box-shadow: ${(props) =>
    props.$isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  margin-bottom: 0;
  /* Hold a single compositing layer so toggling backdrop-filter no longer
     creates and destroys one on every state change (the visible flash). */
  will-change: backdrop-filter;
  /* The header must never be picked as the scroll-anchor node. */
  overflow-anchor: none;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
  }
`;

export const LogoIcon = styled.div<{
  isLogo?: boolean;
  backgroundColor?: string;
}>`
  width: ${(props) => (props.isLogo ? "auto" : "48px")};
  max-width: ${(props) => (props.isLogo ? "150px" : "48px")};
  height: 48px;
  background: ${(props) =>
    props.backgroundColor || "linear-gradient(135deg, #0ea5e9, #0284c7)"};
  border-radius: ${(props) => (props.isLogo ? "8px" : "50%")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  overflow: hidden;
  transition: all 0.2s ease;
  padding: ${(props) => (props.isLogo ? "8px" : "0")};
`;

export const LogoImage = styled.img`
  max-width: 150px;
  max-height: 48px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 6px;
`;
const LogoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.h1<{ textColor?: string }>`
  font-size: 28px;
  font-weight: 800;
  color: ${(props) => props.textColor || "#1e293b"};
  margin: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BrandTagline = styled.p<{ textColor?: string }>`
  font-size: 14px;
  color: ${(props) => props.textColor || "#64748b"};
  margin: 0;
  margin-top: 10px;
  margin-top: 5px;
  font-weight: 500;
`;

const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 12px;
    flex: 1;
    width: 100%;
    padding-top: 20px;
  }
`;

const FundraiserPageBadge = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const PendingBadge = styled(FundraiserPageBadge)`
  background: rgba(59, 130, 246, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
`;

const ExpirationBadge = styled(FundraiserPageBadge)`
  background: rgba(220, 38, 38, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
`;

const DonateButton = styled.button<{ customColors?: any; buttonText?: string }>`
  color: ${(props) => props.customColors?.background || "#ffffff"};
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: ${(props) => {
    const buttonText = props.buttonText || "Donate";
    return Math.max(150, buttonText.length * 10) + "px";
  }};
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

interface HeaderProps {
  onDonateClick?: () => void;
  onShareClick?: () => void;
  customColors?: FundraiserCustomColorConfig;
  isDisabled?: boolean;
  isPreview?: boolean;
  isPromoterInvite?: boolean;
  navigate: NavigateFunction;
}

const Header: React.FC<HeaderProps> = observer(
  ({
    onDonateClick,
    onShareClick,
    customColors,
    isDisabled = false,
    isPreview = false,
    isPromoterInvite = false,
    navigate,
  }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const isScrolledRef = useRef(false);

    // Use live builder data when available, otherwise fall back to saved fundraiser data
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;

    // const hasLiveBuilderData =
    //   builderData.id &&
    //   selectedFundraiser &&
    //   builderData.id === selectedFundraiser.id;
    const hasLiveBuilderData = fundraiserStore.selectedFundraiserId === null;
    // Get custom colors from campaign store if not provided as props
    const builderColors = builderData?.customColors;
    const colors = customColors || builderColors;

    // Ensure text color exists with fallback
    const textColor = colors.text.rgba || colors.text.hex;
    const lighterTextColor = lightenColor(
      colors.text.hex || colors.text.rgba,
      25,
    );

    // Badge logic with priority (Pending takes priority over Expiration)
    const shouldShowPendingBadgeInHeader = shouldShowPendingBadge(
      selectedFundraiser?.startDate || null,
      selectedFundraiser?.status || "",
    );
    const shouldShowExpirationBadgeInHeader =
      !shouldShowPendingBadgeInHeader &&
      shouldShowExpirationBadge(
        selectedFundraiser?.endDate || null,
        selectedFundraiser?.status,
      );
    // Get button text from builder data
    const getButtonText = () => {
      return builderData?.ctaButtonText || "Donate";
    };

    useEffect(() => {
      let rafId: number | null = null;

      const evaluate = () => {
        rafId = null;
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        // Hysteresis: once collapsed, stay collapsed until well back up top.
        const next = isScrolledRef.current
          ? scrollTop > SCROLL_EXPAND_BELOW
          : scrollTop > SCROLL_COLLAPSE_AT;

        // Guarded setter: no state write (and no styled-components class
        // churn) unless the boolean actually changed.
        if (next !== isScrolledRef.current) {
          isScrolledRef.current = next;
          setIsScrolled(next);
        }
      };

      const handleScroll = () => {
        // Coalesce the burst of scroll events a single gesture produces into
        // at most one measurement per frame.
        if (rafId === null) {
          rafId = window.requestAnimationFrame(evaluate);
        }
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      evaluate(); // settle the initial state for a page restored mid-scroll

      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
      };
    }, []);

    const isDonationAllowed = () => {
      // Check if fundraiser is not active or is pending
      return fundraiserStore.isDonationAllowed(
        shouldShowPendingBadgeInHeader,
        selectedFundraiser.status,
      );
    };

    const handleDonateClick = () => {
      if (!isDonationAllowed()) {
        return;
      }
      // Check required fields for builder context
      if (!isPromoterInvite && fundraiserStore.builderData) {
        const hasRequiredName =
          fundraiserStore.builderData.name &&
          fundraiserStore.builderData.name.trim() !== "";
        const hasRequiredTitle =
          fundraiserStore.builderData.title &&
          fundraiserStore.builderData.title.trim() !== "";
        const hasRequiredStartDate = fundraiserStore.builderData.startDate;
        const hasRequiredMedia =
          fundraiserStore.builderData.mediaItems &&
          fundraiserStore.builderData.mediaItems.length > 0;

        if (
          !hasRequiredName ||
          !hasRequiredTitle ||
          !hasRequiredStartDate ||
          !hasRequiredMedia
        ) {
          return; // Don't allow donation if required fields missing
        }
      }

      if (isPromoterInvite) {
        // For promoter invite page, navigate to full fundraiser page
        const fundraiserId =
          fundraiserStore.selectedFundraiserId || selectedFundraiser?.id;
        if (fundraiserId) {
          fundraiserStore.setSelectedFundraiser(fundraiserId);
          navigate(`/fundraiser/${fundraiserId}`);
        }
      } else if (onDonateClick) {
        onDonateClick();
      } else {
        // Fallback to scroll behavior - look for donation capture form
        const donationCapture = document.getElementById(
          "donation-capture-header",
        );
        if (donationCapture) {
          const targetPosition =
            donationCapture.getBoundingClientRect().top + window.scrollY - 125;
          window.scrollTo({ top: targetPosition, behavior: "smooth" });
        }
      }
    };

    // Get fundraiser title and tagline from builder data or fundraiser data
    const getFundraiserTitle = () => {
      if (hasLiveBuilderData && builderData.title) {
        return builderData.title;
      }
      return selectedFundraiser?.title || ""; // "Clean Water for All";
    };

    const getFundraiserTagline = () => {
      if (hasLiveBuilderData && builderData.subtitle) {
        return builderData.subtitle;
      }
      return selectedFundraiser?.subtitle || "";
    };

    // Get icon from builder data
    const getFundraiserIcon = () => {
      const iconImageChoice = hasLiveBuilderData
        ? builderData.iconImageChoice || IconType.None
        : selectedFundraiser?.iconImageChoice || IconType.None;

      if (iconImageChoice === IconType.Icon) {
        const iconName = hasLiveBuilderData
          ? builderData.titleIcon || "" //|| "Droplets"
          : selectedFundraiser?.titleIcon || ""; //|| "Droplets";
        return iconName;
      }

      // Default fallback icon
      return ""; // "Droplets";
    };

    // Get fundraiser image from builder data
    const getFundraiserImage = (): string | null => {
      if (hasLiveBuilderData) {
        return builderData.fundraiserImage.fullURL || null;
      }
      return selectedFundraiser?.fundraiserImage.fullURL || null;
    };

    // Get icon color from builder data
    const getIconColor = (): ColorModel => {
      if (hasLiveBuilderData) {
        return (
          builderData.titleColor ||
          new ColorModel({
            hex: "#0ea5e9",
            rgbValue: { r: "14", g: "165", b: "233", a: "1" },
            rgba: "rgba(14, 165, 233, 1)",
          })
        );
      }
      return (
        selectedFundraiser?.titleColor ||
        new ColorModel({
          hex: "#0ea5e9",
          rgbValue: { r: "14", g: "165", b: "233", a: "1" },
          rgba: "rgba(14, 165, 233, 1)",
        })
      );
    };

    // Check if we should show icon or image
    const shouldShowImage = () => {
      const iconImageChoice = hasLiveBuilderData
        ? builderData.iconImageChoice || IconType.None
        : selectedFundraiser?.iconImageChoice || IconType.None;
      const fundraiserImage = getFundraiserImage();
      return iconImageChoice === IconType.Image && fundraiserImage
        ? true
        : false;
    };

    // Get icon colors based on background
    const getIconColors = () => {
      const selectedColor = getIconColor();
      const bgColor = selectedColor;
      const iconColor = isLightColor(selectedColor.hex)
        ? darkenColor(selectedColor.hex)
        : "#ffffff";
      return { bgColor, iconColor };
    };

    const renderIcon = (iconName: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={28} /> : <Droplets size={28} />;
    };

    const handleShareClick = () => {
      if (onShareClick) {
        onShareClick();
      }
    };

    const handleJoinClick = () => {
      if (isPromoterInvite) {
        // Scroll to join form on promoter invite page
        const joinForm = document.querySelector("[data-join-form]");
        if (joinForm) {
          const targetPosition =
            joinForm.getBoundingClientRect().top + window.scrollY - 125;
          window.scrollTo({ top: targetPosition, behavior: "smooth" });
        }
      }
    };

    return (
      <HeaderContainer $isScrolled={isScrolled} customColors={colors}>
        {" "}
        {/* Removed duplicate HeaderContainer */}
        <HeaderContent>
          <Logo>
            <LogoIcon
              backgroundColor={
                shouldShowImage()
                  ? "transparent"
                  : getIconColors().bgColor.rgba || getIconColors().bgColor.hex
              }
              isLogo={shouldShowImage()}
            >
              {shouldShowImage() ? (
                <LogoImage
                  src={getFundraiserImage() || ""}
                  alt={getFundraiserTitle()}
                />
              ) : (
                <div style={{ color: getIconColors().iconColor }}>
                  {renderIcon(getFundraiserIcon())}
                </div>
              )}
            </LogoIcon>
            <LogoText>
              <BrandName textColor={textColor}>
                {getFundraiserTitle()}
                {shouldShowPendingBadgeInHeader &&
                  selectedFundraiser?.startDate && (
                    <PendingBadge>
                      {getPendingBadgeText(
                        new Date(selectedFundraiser.startDate),
                      )}
                    </PendingBadge>
                  )}
                {shouldShowExpirationBadgeInHeader &&
                  selectedFundraiser?.endDate && (
                    <ExpirationBadge>
                      {getExpirationBadgeText(
                        new Date(selectedFundraiser.endDate),
                      )}
                    </ExpirationBadge>
                  )}
              </BrandName>
              {getFundraiserTagline() && (
                <BrandTagline textColor={lighterTextColor}>
                  {getFundraiserTagline()}
                </BrandTagline>
              )}
            </LogoText>
          </Logo>

          <HeaderCenter>
            {onShareClick && (
              <button
                style={{
                  padding: "16px 24px",
                  height: "56px",
                  background: "#ffffff",
                  color: "#64748b",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
                onClick={handleShareClick}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f8fafc";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#ffffff";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                <Share2 size={16} />
                Share
              </button>
            )}

            <DonateButton
              onClick={handleDonateClick}
              customColors={colors}
              buttonText={getButtonText()}
              disabled={!isDonationAllowed()}
              style={{
                ...fundraiserStore.getDonationButtonStyles(),
                minWidth: "fit-content",
                height: "56px",
                padding: "16px 24px",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (!isDonationAllowed()) return;
                e.currentTarget.style.boxShadow =
                  fundraiserStore.getDonationButtonStyles()["--hover-shadow"];
              }}
              onMouseLeave={(e) => {
                if (!isDonationAllowed()) return;
                e.currentTarget.style.boxShadow =
                  fundraiserStore.getDonationButtonStyles().boxShadow as string;
              }}
            >
              {getButtonText()}
            </DonateButton>
          </HeaderCenter>
        </HeaderContent>
      </HeaderContainer>
    );
  },
);

export default withRouter(Header);
